import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TaskCard } from "./TaskCard";
import { Task, TaskStatus } from "@/types";
import { cn } from "@/lib/utils";
import { getColorFromTailwind } from "@/utils/colorUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, X, Check, Trash2, GripVertical } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

const colorOptions = [
  { name: "Blue", value: "bg-blue-500" },
  { name: "Green", value: "bg-green-500" },
  { name: "Yellow", value: "bg-yellow-500" },
  { name: "Orange", value: "bg-orange-500" },
  { name: "Red", value: "bg-red-500" },
  { name: "Purple", value: "bg-purple-500" },
  { name: "Pink", value: "bg-pink-500" },
  { name: "Indigo", value: "bg-indigo-500" },
  { name: "Teal", value: "bg-teal-500" },
  { name: "Cyan", value: "bg-cyan-500" },
];

interface TaskColumnProps {
  status: string;
  statusColor?: string;
  statusId?: string;
  applyColorToCards?: boolean;
  projectId: string;
  tasks: Task[];
  onDeleteTask: (id: string) => void;
  onViewTaskDetails?: (task: Task) => void;
  onEditTask?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
  onCreateTask?: (status: string) => void;
  onQuickCreateTask?: (status: string, title: string, description?: string) => void;
  onDeleteStatus?: () => void;
  onEditStatus?: (statusId: string, newName: string, newColor: string, applyColorToCards?: boolean) => void;
}

export const TaskColumn = ({
  status,
  statusColor = "border-t-primary",
  statusId,
  applyColorToCards = false,
  projectId,
  tasks,
  onDeleteTask,
  onViewTaskDetails,
  onEditTask,
  onUpdateTask,
  onCreateTask,
  onQuickCreateTask,
  onDeleteStatus,
  onEditStatus,
}: TaskColumnProps) => {
  const { setNodeRef } = useDroppable({ id: status });
  
  // Make column sortable if statusId is provided
  const {
    attributes,
    listeners,
    setNodeRef: setSortableNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: statusId || status,
    disabled: !statusId, // Only allow dragging if statusId exists
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Combine refs
  const combinedRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    setSortableNodeRef(node);
  };
  
  const [showAddButton, setShowAddButton] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [editedStatusName, setEditedStatusName] = useState(status);
  const [editedStatusColor, setEditedStatusColor] = useState(statusColor || "bg-blue-500");
  const [editedApplyColorToCards, setEditedApplyColorToCards] = useState(applyColorToCards ?? false);
  const inputRef = useRef<HTMLInputElement>(null);
  const statusInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isCreatingTask && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreatingTask]);

  useEffect(() => {
    if (isEditingStatus && statusInputRef.current) {
      statusInputRef.current.focus();
      statusInputRef.current.select();
    }
  }, [isEditingStatus]);

  return (
    <div 
      ref={combinedRef}
      style={style}
      className="flex flex-col w-80 flex-1 shrink-0 h-full"
    >
      <div
        className="mb-4 rounded-lg border-t-4 bg-card p-2 sm:p-3 shrink-0"
        style={{ borderTopColor: statusColor ? getColorFromTailwind(statusColor) : undefined }}
      >
        <div className="flex items-center justify-between gap-2">
          {/* Drag handle - only show if statusId exists and not editing */}
          {statusId && !isEditingStatus && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
              title="Drag to reorder status"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          {isEditingStatus && onEditStatus && statusId ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                ref={statusInputRef}
                type="text"
                value={editedStatusName}
                onChange={(e) => setEditedStatusName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (statusId && editedStatusName.trim() && onEditStatus) {
                      onEditStatus(statusId, editedStatusName.trim(), editedStatusColor, editedApplyColorToCards);
                    }
                    setIsEditingStatus(false);
                  } else if (e.key === 'Escape') {
                    setEditedStatusName(status);
                    setEditedStatusColor(statusColor || "bg-blue-500");
                    setEditedApplyColorToCards(applyColorToCards ?? false);
                    setIsEditingStatus(false);
                  }
                }}
                className="h-7 text-sm font-semibold"
                onBlur={(e) => {
                  // Don't close if clicking on color buttons or switch
                  const relatedTarget = e.relatedTarget as HTMLElement;
                  if (relatedTarget && (
                    relatedTarget.closest('.color-button') || 
                    relatedTarget.closest('[role="switch"]') ||
                    relatedTarget.closest('button')
                  )) {
                    return;
                  }
                  
                  // Save changes when blurring (clicking outside)
                  if (statusId && editedStatusName.trim() && onEditStatus) {
                    onEditStatus(statusId, editedStatusName.trim(), editedStatusColor, editedApplyColorToCards);
                  }
                  setIsEditingStatus(false);
                }}
              />
              <div className="flex gap-1 flex-wrap">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className="color-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const newColor = color.value;
                      setEditedStatusColor(newColor);
                      if (onEditStatus && statusId && editedStatusName.trim()) {
                        onEditStatus(statusId, editedStatusName.trim(), newColor, editedApplyColorToCards);
                      }
                    }}
                    onMouseDown={(e) => {
                      // Prevent input blur when clicking color button
                      e.preventDefault();
                    }}
                    className={cn(
                      "color-button w-5 h-5 rounded-full border-2 transition-all",
                      color.value,
                      editedStatusColor === color.value ? "border-primary ring-1 ring-primary scale-110" : "border-transparent"
                    )}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor={`apply-color-${statusId}`} className="text-xs whitespace-nowrap">Apply</Label>
                <Switch
                  id={`apply-color-${statusId}`}
                  checked={editedApplyColorToCards}
                  onCheckedChange={(checked) => {
                    setEditedApplyColorToCards(checked);
                    if (onEditStatus && statusId && editedStatusName.trim()) {
                      onEditStatus(statusId, editedStatusName.trim(), editedStatusColor, checked);
                    }
                  }}
                  onMouseDown={(e) => {
                    // Prevent input blur when clicking switch
                    e.preventDefault();
                  }}
                />
              </div>
            </div>
          ) : (
            <h3 
              className="text-xs sm:text-sm font-semibold text-foreground truncate cursor-pointer hover:opacity-70 transition-opacity"
              onDoubleClick={() => {
                if (onEditStatus && statusId) {
                  setIsEditingStatus(true);
                  setEditedStatusName(status);
                  setEditedStatusColor(statusColor || "bg-blue-500");
                  setEditedApplyColorToCards(applyColorToCards ?? false);
                }
              }}
              title={onEditStatus ? "Double-click to edit" : undefined}
            >
              {status}
            </h3>
          )}
          {!isEditingStatus && (
            <>
              {tasks.length === 0 && onDeleteStatus ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteStatus();
                  }}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  title="Delete status"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground ml-2">{tasks.length}</span>
              )}
            </>
          )}
        </div>
      </div>
      <div
        ref={setNodeRef}
        className="flex-1 space-y-2 rounded-lg bg-muted/30 p-1.5 sm:p-2 pb-8 min-h-0 flex flex-col"
        onMouseEnter={() => setShowAddButton(true)}
        onMouseLeave={() => setShowAddButton(false)}
      >
        <div className="flex-1 space-y-2">
          {tasks.length > 0 ? (
            <SortableContext
              items={tasks.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              {tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  projectId={projectId}
                  statusColor={statusColor}
                  applyColorToCards={applyColorToCards}
                  onDelete={onDeleteTask}
                  onViewDetails={onViewTaskDetails}
                  onEditTask={onEditTask}
                  onUpdateTask={onUpdateTask}
                />
              ))}
            </SortableContext>
          ) : null}
          
          {/* Add Here Button or Inline Task Creation */}
          {(showAddButton || tasks.length === 0) && !isCreatingTask && onCreateTask && (
            <div className="shrink-0">
              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-muted/50 border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 transition-all"
                onClick={() => {
                  setIsCreatingTask(true);
                  setNewTaskTitle("");
                  setNewTaskDescription("");
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Here
              </Button>
            </div>
          )}
          
          {/* Inline Task Creation Form */}
          {isCreatingTask && (
            <div className="shrink-0 rounded-lg border bg-card p-3 shadow-sm">
              <Input
                ref={inputRef}
                placeholder="Enter task title..."
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newTaskTitle.trim()) {
                    // Enter to submit directly
                    e.preventDefault();
                    if (onQuickCreateTask) {
                      onQuickCreateTask(status, newTaskTitle.trim(), newTaskDescription.trim());
                    }
                    setIsCreatingTask(false);
                    setNewTaskTitle("");
                    setNewTaskDescription("");
                  } else if (e.key === "Escape") {
                    setIsCreatingTask(false);
                    setNewTaskTitle("");
                    setNewTaskDescription("");
                  }
                }}
                className="mb-2"
              />
              <Textarea
                placeholder="Enter description (optional)..."
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && newTaskTitle.trim()) {
                    // Ctrl/Cmd + Enter to submit from textarea
                    e.preventDefault();
                    if (onQuickCreateTask) {
                      onQuickCreateTask(status, newTaskTitle.trim(), newTaskDescription.trim());
                    }
                    setIsCreatingTask(false);
                    setNewTaskTitle("");
                    setNewTaskDescription("");
                  } else if (e.key === "Escape") {
                    setIsCreatingTask(false);
                    setNewTaskTitle("");
                    setNewTaskDescription("");
                  }
                }}
                className="mb-2 min-h-[60px] resize-none"
                rows={2}
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    if (newTaskTitle.trim() && onQuickCreateTask) {
                      onQuickCreateTask(status, newTaskTitle.trim(), newTaskDescription.trim());
                    }
                    setIsCreatingTask(false);
                    setNewTaskTitle("");
                    setNewTaskDescription("");
                  }}
                  disabled={!newTaskTitle.trim()}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsCreatingTask(false);
                    setNewTaskTitle("");
                    setNewTaskDescription("");
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
